import { useEffect, useMemo, useState } from "react";
import {
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { Add, Close, Search } from "@mui/icons-material";
import { nanoid } from "nanoid";
import clsx from "clsx";
import { categorizeHazard, hazardCategories } from "../../constants/hazardCategoryConstants";

const useStyles = makeStyles((theme) => ({
    titleBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2, 3, 0),
    },
    title: {
        fontFamily: ["Sailec", "Arial", "sans-serif"].join(","),
        fontWeight: 700,
    },
    search: {
        margin: theme.spacing(2, 0),
    },
    layout: {
        display: "grid",
        gap: theme.spacing(3),
        gridTemplateColumns: "1fr",
        [theme.breakpoints.up("md")]: {
            gridTemplateColumns: "232px 1fr 280px",
        },
    },
    sidebar: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    catButton: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        width: "100%",
        font: "inherit",
        color: theme.palette.text.primary,
        textAlign: "left",
        padding: theme.spacing(1, 1.25),
        border: "none",
        borderRadius: theme.spacing(1),
        backgroundColor: "transparent",
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    catButtonActive: {
        backgroundColor: "rgba(88, 72, 201, 0.10)",
        color: theme.palette.secondary.main,
        fontWeight: 600,
    },
    catIcon: {
        color: theme.palette.secondary.main,
        display: "flex",
    },
    catLabel: {
        flex: 1,
        fontSize: "0.85rem",
        lineHeight: 1.2,
    },
    catCount: {
        color: theme.palette.text.secondary,
        fontSize: "0.8rem",
        fontVariantNumeric: "tabular-nums",
    },
    centerHeader: {
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
    },
    checkLabel: {
        alignItems: "flex-start",
        marginLeft: 0,
        "& .MuiFormControlLabel-label": {
            fontSize: "0.9rem",
            paddingTop: 6,
        },
    },
    centerScroll: {
        maxHeight: 360,
        overflowY: "auto",
        paddingRight: theme.spacing(1),
    },
    selectedPanel: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
    },
    selectedTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
    },
    selectedList: {
        flex: 1,
        maxHeight: 300,
        overflowY: "auto",
        marginBottom: theme.spacing(2),
    },
    selectedItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 0),
    },
    selectedName: {
        flex: 1,
        fontSize: "0.85rem",
    },
    emptySelected: {
        color: theme.palette.text.secondary,
        fontSize: "0.85rem",
    },
    customRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: "auto",
    },
    empty: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(3),
        textAlign: "center",
    },
}));

type HazardLike = { id: any; name: string };
type CustomControl = { id: any; name: string };
type CustomHazard = { id: any; name: string; siteExternalId?: string; controls?: CustomControl[] };

interface HazardPickerDialogProps {
    open: boolean;
    onClose: () => void;
    hazards: HazardLike[];
    selectedHazardIds: any[];
    customHazards: CustomHazard[];
    siteExternalId?: string;
    onSave: (result: { hazardIds: any[]; customHazards: CustomHazard[] }) => void;
}

export default function HazardPickerDialog({
    open,
    onClose,
    hazards,
    selectedHazardIds,
    customHazards,
    siteExternalId = "",
    onSave,
}: HazardPickerDialogProps) {
    const classes = useStyles();

    const [draftIds, setDraftIds] = useState<any[]>([]);
    const [draftCustom, setDraftCustom] = useState<CustomHazard[]>([]);
    const [activeCategory, setActiveCategory] = useState(hazardCategories[0].key);
    const [search, setSearch] = useState("");
    const [newCustomName, setNewCustomName] = useState("");

    // Reset the working draft each time the dialog opens. Existing controls on a custom hazard
    // are preserved here — they're edited on the main template builder page, not in this picker.
    useEffect(() => {
        if (open) {
            setDraftIds([...(selectedHazardIds ?? [])]);
            setDraftCustom((customHazards ?? []).map((c) => ({ ...c, controls: c.controls ?? [] })));
            setSearch("");
            setNewCustomName("");
            setActiveCategory(hazardCategories[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const allHazards = hazards ?? [];

    const countsByCategory = useMemo(() => {
        const counts: Record<string, number> = {};
        allHazards.forEach((h) => {
            const key = categorizeHazard(h.name);
            counts[key] = (counts[key] ?? 0) + 1;
        });
        return counts;
    }, [allHazards]);

    const trimmedSearch = search.trim().toLowerCase();
    const visibleHazards = useMemo(() => {
        if (trimmedSearch) {
            return allHazards.filter((h) => h.name.toLowerCase().includes(trimmedSearch));
        }
        return allHazards.filter((h) => categorizeHazard(h.name) === activeCategory);
    }, [allHazards, trimmedSearch, activeCategory]);

    const activeCategoryLabel =
        hazardCategories.find((c) => c.key === activeCategory)?.label ?? "";

    function toggleHazard(id: any) {
        setDraftIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
    }

    function addCustomHazard() {
        const name = newCustomName.trim();
        if (!name) return;
        const exists =
            draftCustom.some((c) => c.name.trim().toLowerCase() === name.toLowerCase()) ||
            allHazards.some((h) => h.name.trim().toLowerCase() === name.toLowerCase());
        if (exists) return;
        setDraftCustom((customs) => [...customs, { id: nanoid(5), name, siteExternalId, controls: [] }]);
        setNewCustomName("");
    }

    function removeCustomHazard(id: any) {
        setDraftCustom((customs) => customs.filter((c) => c.id !== id));
    }

    const selectedHazards = allHazards.filter((h) => draftIds.includes(h.id));
    const totalSelected = selectedHazards.length + draftCustom.length;

    function handleSave() {
        onSave({ hazardIds: draftIds, customHazards: draftCustom });
        onClose();
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="hazard-picker-title">
            <div className={classes.titleBar}>
                <Typography variant="h5" className={classes.title} id="hazard-picker-title">
                    Add Hazard
                </Typography>
                <IconButton onClick={onClose} aria-label="Close">
                    <Close />
                </IconButton>
            </div>
            <DialogContent>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search all hazards"
                    className={classes.search}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                <div className={classes.layout}>
                    <div className={classes.sidebar}>
                        <Typography variant="overline" color="textSecondary">
                            All Categories
                        </Typography>
                        {hazardCategories.map((category) => {
                            const CategoryIcon = category.Icon;
                            const active = !trimmedSearch && category.key === activeCategory;
                            return (
                                <button
                                    key={category.key}
                                    type="button"
                                    onClick={() => {
                                        setSearch("");
                                        setActiveCategory(category.key);
                                    }}
                                    className={clsx(classes.catButton, { [classes.catButtonActive]: active })}
                                >
                                    <span className={classes.catIcon}>
                                        <CategoryIcon fontSize="small" />
                                    </span>
                                    <span className={classes.catLabel}>{category.label}</span>
                                    <span className={classes.catCount}>{countsByCategory[category.key] ?? 0}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div>
                        <Typography variant="subtitle1" className={classes.centerHeader}>
                            {trimmedSearch ? "Search results" : activeCategoryLabel}
                        </Typography>
                        {visibleHazards.length === 0 ? (
                            <Typography className={classes.empty}>
                                {trimmedSearch ? "No hazards match your search." : "No hazards in this category."}
                            </Typography>
                        ) : (
                            <div className={classes.centerScroll}>
                                <Grid container spacing={0.5}>
                                    {visibleHazards.map((hazard) => (
                                        <Grid size={{ xs: 12, sm: 6 }} key={hazard.id}>
                                            <FormControlLabel
                                                className={classes.checkLabel}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={draftIds.includes(hazard.id)}
                                                        onChange={() => toggleHazard(hazard.id)}
                                                    />
                                                }
                                                label={hazard.name}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        )}
                    </div>

                    <div className={classes.selectedPanel}>
                        <Typography variant="subtitle1" className={classes.selectedTitle}>
                            Selected Hazards {totalSelected > 0 ? `(${totalSelected})` : ""}
                        </Typography>
                        <div className={classes.selectedList}>
                            {totalSelected === 0 ? (
                                <Typography className={classes.emptySelected}>No hazard selected yet.</Typography>
                            ) : (
                                <>
                                    {selectedHazards.map((hazard) => (
                                        <div className={classes.selectedItem} key={`h-${hazard.id}`}>
                                            <Typography className={classes.selectedName}>{hazard.name}</Typography>
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${hazard.name}`}
                                                onClick={() => toggleHazard(hazard.id)}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </div>
                                    ))}
                                    {draftCustom.map((custom) => (
                                        <div className={classes.selectedItem} key={`c-${custom.id}`}>
                                            <Typography className={classes.selectedName}>{custom.name}</Typography>
                                            <Chip size="small" label="Custom" color="secondary" variant="outlined" />
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${custom.name}`}
                                                onClick={() => removeCustomHazard(custom.id)}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                        <div className={classes.customRow}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Add custom hazard"
                                value={newCustomName}
                                onChange={(e) => setNewCustomName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addCustomHazard();
                                    }
                                }}
                            />
                            <Button
                                color="secondary"
                                startIcon={<Add />}
                                disabled={!newCustomName.trim()}
                                onClick={addCustomHazard}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ padding: 2 }}>
                <Button variant="outlined" color="primary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
